import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { clientApi } from '@/config/axios';
import { useAuth } from '@/lib/auth/AuthContext';

export interface MemberOut {
  id: string;
  is_active: boolean;
  is_consultant: boolean;
  joined_at: string;
  organization_id: string;
  role_id: string;
  user_id: string;
  user_name: string | null;
  user_email: string | null;
  role_name: string | null;
}

function orgUrl(orgId: string) {
  return `/api/v1/organizations/${orgId}/members`;
}

export function useMembers() {
  const { organization } = useAuth();
  const orgId = organization?.organization_id;

  return useQuery({
    enabled: !!orgId,
    queryKey: ['members', orgId],
    queryFn: async () => {
      const { data } = await clientApi.get<MemberOut[]>(orgUrl(orgId!));
      return data;
    },
  });
}

export function useInviteMember() {
  const { organization } = useAuth();
  const orgId = organization?.organization_id;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: { email: string; name: string; role_id: string }) => {
      const { data } = await clientApi.post<MemberOut>(orgUrl(orgId!), body);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['members', orgId] });
      queryClient.invalidateQueries({ queryKey: ['invitations', orgId] });
    },
  });
}

export function useUpdateMember() {
  const { organization } = useAuth();
  const orgId = organization?.organization_id;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...body }: { id: string; role_id?: string; is_active?: boolean; is_consultant?: boolean }) => {
      const { data } = await clientApi.patch<MemberOut>(`${orgUrl(orgId!)}/${id}`, body);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['members', orgId] });
    },
  });
}

export function useRemoveMember() {
  const { organization } = useAuth();
  const orgId = organization?.organization_id;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (memberId: string) => {
      await clientApi.delete(`${orgUrl(orgId!)}/${memberId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['members', orgId] });
    },
  });
}
